/**
 * Wave 5 — Clarification impact, addenda incorporation, negotiation round compare.
 * Deterministic from domain fields — no invented %.
 */

export type ImpactLevel = 'NONE' | 'LOW' | 'MEDIUM' | 'HIGH';

export type ImpactBadge = {
  key: string;
  label: string;
  color: 'default' | 'processing' | 'success' | 'warning' | 'error';
};

export type ClarificationImpact = {
  level: ImpactLevel;
  badges: ImpactBadge[];
  reasons: string[];
};

export type AddendumIncorporation = {
  incorporated: boolean;
  affectsEstimate: boolean;
  status: string;
  badges: ImpactBadge[];
  reasons: string[];
  canMarkIncorporated: boolean;
};

export type NegotiationRoundCompare = {
  leftRound: number;
  rightRound: number;
  leftValue: number | null;
  rightValue: number | null;
  valueDelta: number | null;
  valueDeltaPct: number | null; // only when both values present — computed, not fabricated readiness
  outcomeChanged: boolean;
  leftOutcome: string;
  rightOutcome: string;
  termDiffs: Array<{ key: string; left: string; right: string }>;
  notesChanged: boolean;
};

function asStr(v: unknown): string {
  return v == null ? '' : String(v).trim();
}

function parseJson(raw: unknown): unknown {
  if (raw == null) return null;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return raw;
}

function parseDue(raw: string): Date | null {
  if (!raw) return null;
  const d = new Date(raw.length <= 10 ? `${raw}T00:00:00` : raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function truthyFlag(v: unknown): boolean {
  return v === true || v === 1 || v === '1' || String(v).toLowerCase() === 'true';
}

/**
 * Impact badges for a clarification row (overdue, open, answered, meta impact areas).
 */
export function buildClarificationImpact(
  row: Record<string, unknown>,
  opts?: { today?: Date }
): ClarificationImpact {
  const today = startOfDay(opts?.today ?? new Date());
  const status = asStr(row.status || 'Open').toLowerCase();
  const dueRaw = asStr(row.due_at || row.dueAt);
  const response = asStr(row.response);
  const question = asStr(row.question || row.subject);
  const badges: ImpactBadge[] = [];
  const reasons: string[] = [];
  let level: ImpactLevel = 'NONE';

  const due = parseDue(dueRaw);
  const overdue =
    Boolean(due) &&
    due! < today &&
    !['answered', 'closed', 'cancelled'].includes(status) &&
    !response;

  if (overdue) {
    badges.push({ key: 'overdue', label: 'Overdue', color: 'error' });
    reasons.push('Response due date has passed while still open');
    level = 'HIGH';
  }

  if (status === 'open' || status === 'pending') {
    badges.push({ key: 'open', label: 'Open', color: 'processing' });
    reasons.push('Clarification still open');
    if (level === 'NONE') level = 'MEDIUM';
  }

  if (response || status === 'answered' || status === 'closed') {
    badges.push({ key: 'answered', label: 'Answered', color: 'success' });
    if (level === 'NONE') level = 'LOW';
  }

  if (!question) {
    badges.push({ key: 'incomplete', label: 'No question', color: 'warning' });
    reasons.push('Question text missing');
    if (level === 'NONE' || level === 'LOW') level = 'MEDIUM';
  }

  const meta = parseJson(row.meta_json ?? row.metaJson) as Record<string, unknown> | null;
  const areasRaw = meta?.impactAreas ?? meta?.impact_areas ?? meta?.impact;
  const areas: string[] = Array.isArray(areasRaw)
    ? areasRaw.map((a) => asStr(a)).filter(Boolean)
    : typeof areasRaw === 'string' && areasRaw
      ? [areasRaw]
      : [];

  for (const area of areas) {
    const label = area.charAt(0).toUpperCase() + area.slice(1);
    badges.push({
      key: `impact-${area.toLowerCase()}`,
      label: `Impact: ${label}`,
      color: 'warning',
    });
    reasons.push(`Declared impact area: ${label}`);
    if (level === 'NONE' || level === 'LOW') level = 'MEDIUM';
    if (/price|cost|commercial|value/i.test(area) && level !== 'HIGH') {
      level = overdue ? 'HIGH' : 'MEDIUM';
    }
  }

  if (!badges.length) {
    badges.push({ key: 'none', label: 'No impact flags', color: 'default' });
  }

  return { level, badges, reasons };
}

export function impactLevelColor(level: ImpactLevel): string {
  switch (level) {
    case 'HIGH':
      return 'error';
    case 'MEDIUM':
      return 'warning';
    case 'LOW':
      return 'success';
    default:
      return 'default';
  }
}

/**
 * Addendum incorporation state from status + affects_estimate.
 */
export function buildAddendumIncorporation(row: Record<string, unknown>): AddendumIncorporation {
  const status = asStr(row.status || 'Issued');
  const statusNorm = status.toLowerCase();
  const affectsEstimate = truthyFlag(row.affects_estimate ?? row.affectsEstimate);
  const meta = parseJson(row.meta_json ?? row.metaJson) as Record<string, unknown> | null;
  const metaIncorporated = truthyFlag(meta?.incorporated);
  const incorporated =
    metaIncorporated ||
    statusNorm === 'incorporated' ||
    statusNorm === 'closed' ||
    statusNorm === 'acknowledged';

  const badges: ImpactBadge[] = [];
  const reasons: string[] = [];

  if (affectsEstimate) {
    badges.push({ key: 'affects-estimate', label: 'Affects estimate', color: 'warning' });
    reasons.push('Addendum flagged as affecting estimate');
  }

  if (incorporated) {
    badges.push({ key: 'incorporated', label: 'Incorporated', color: 'success' });
    reasons.push(`Status/meta indicates incorporation (${status})`);
  } else {
    badges.push({ key: 'pending', label: 'Not incorporated', color: 'error' });
    reasons.push('Operator must revise estimate/proposal then mark incorporated');
  }

  return {
    incorporated,
    affectsEstimate,
    status,
    badges,
    reasons,
    canMarkIncorporated: !incorporated,
  };
}

function asNumber(v: unknown): number | null {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}

function flattenTerms(raw: unknown): Record<string, string> {
  const parsed = parseJson(raw);
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
    out[k] = v == null ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v);
  }
  return out;
}

/**
 * Compare two negotiation rounds (value delta + term diffs).
 * Percent only when both offered values exist.
 */
export function compareNegotiationRounds(
  left: Record<string, unknown>,
  right: Record<string, unknown>
): NegotiationRoundCompare {
  const leftValue = asNumber(left.offered_value ?? left.offeredValue);
  const rightValue = asNumber(right.offered_value ?? right.offeredValue);
  let valueDelta: number | null = null;
  let valueDeltaPct: number | null = null;
  if (leftValue != null && rightValue != null) {
    valueDelta = rightValue - leftValue;
    if (leftValue !== 0) {
      valueDeltaPct = (valueDelta / leftValue) * 100;
    }
  }

  const leftOutcome = asStr(left.outcome || '—');
  const rightOutcome = asStr(right.outcome || '—');
  const leftTerms = flattenTerms(left.terms_json ?? left.termsJson);
  const rightTerms = flattenTerms(right.terms_json ?? right.termsJson);
  const keys = Array.from(new Set([...Object.keys(leftTerms), ...Object.keys(rightTerms)])).sort();
  const termDiffs = keys
    .filter((k) => (leftTerms[k] || '') !== (rightTerms[k] || ''))
    .map((k) => ({
      key: k,
      left: leftTerms[k] || '—',
      right: rightTerms[k] || '—',
    }));

  return {
    leftRound: asNumber(left.round_number ?? left.roundNumber) ?? 0,
    rightRound: asNumber(right.round_number ?? right.roundNumber) ?? 0,
    leftValue,
    rightValue,
    valueDelta,
    valueDeltaPct,
    outcomeChanged: leftOutcome !== rightOutcome,
    leftOutcome,
    rightOutcome,
    termDiffs,
    notesChanged: asStr(left.notes) !== asStr(right.notes),
  };
}

/** Pick previous vs latest round for default comparison. */
export function pickNegotiationComparePair(
  rounds: Record<string, unknown>[]
): { left: Record<string, unknown>; right: Record<string, unknown> } | null {
  if (rounds.length < 2) return null;
  const sorted = [...rounds].sort(
    (a, b) =>
      (asNumber(a.round_number ?? a.roundNumber) || 0) -
      (asNumber(b.round_number ?? b.roundNumber) || 0)
  );
  return {
    left: sorted[sorted.length - 2],
    right: sorted[sorted.length - 1],
  };
}
